import { Component, ViewChild } from '@angular/core';
import { BatchConfigListItem, BatchProcess, MergedExecutionItem, OrgBatchTemplate } from '../models/batch'
import { AlertService } from 'src/app/components/_alert/alert.service';
import { ESystemAccess, ESystemProfileList } from 'src/app/enum/EAccess';
import { SessionService } from 'src/app/shared/services/session.service';
import { AdministracionCarteraApiService } from '../services/administracion-cartera-api.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, forkJoin } from 'rxjs';
import { BagResponse } from '../models/bag';
import { ExecDetailModalComponent } from '../components/exec-detail-modal/exec-detail-modal.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormBuilder, Validators } from '@angular/forms';
import { catchError, tap } from 'rxjs/operators';

@Component({
    selector: 'app-admin-cartera-execution-panel',
    templateUrl: './admin-cartera-execution-panel.component.html',
    styleUrls: ['./admin-cartera-execution-panel.component.scss']
})
export class AdminCarteraExecutionPanelComponent {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    private readonly access: string[] = [
        ESystemAccess.ADMINISTRACION_CARTERA,
        ESystemProfileList.WEB
    ];

    private id: string | null = null;
    
    private procesos: MergedExecutionItem[] = [];
    private batchConfigs: BatchConfigListItem[] = []

    public detail: BagResponse | null = null;
    public modelos: OrgBatchTemplate[] = [];

    public filtersForm = this.fb.nonNullable.group({
        bagId: [''],
        batchTemplateTag: ['', Validators.required],
        dateFrom: [''],
        dateTo: ['']
    });

    public dataSource = new MatTableDataSource<MergedExecutionItem>();
    public displayedColumns = ['fechaEjecucion', 'id', 'modelo', 'estado', 'frecuencia', 'acciones'];

    public isProcessesListLoading = false
    public isModelosListLoading = false

    constructor(
        private _alertsService: AlertService,
        private _apiService: AdministracionCarteraApiService,
        private _router: Router,
        private _session: SessionService,
        private _route: ActivatedRoute,
        private fb: FormBuilder,
        public dialog: MatDialog,
    ) { }

    ngOnInit(): void {
        if (!this.checkAccess()) {
            this._router.navigate(['/request-access'])
            return;
        }

        const twoDaysBeforeDate = this.getDateTwoDaysBefore();
        this.filtersForm.patchValue({ dateFrom: twoDaysBeforeDate });

        this._route.paramMap.subscribe(params => {
            this.id = params.get('id');
            const modelId = this._route.snapshot.queryParamMap.get('modelId');

            if (this.id) {
                this.getBagDetail(this.id);

                this.getBagData(this.id).subscribe({
                    next: () => {
                        if (modelId) {
                            this.filtersForm.patchValue({ batchTemplateTag: modelId });
                            this.searchProcesses();
                        }
                    }
                });
            }
        });
    }

    getDateTwoDaysBefore(): string {
        const twoDaysBefore = new Date();
        twoDaysBefore.setDate(twoDaysBefore.getDate() - 2);

        const year = twoDaysBefore.getFullYear();
        const month = String(twoDaysBefore.getMonth() + 1).padStart(2, '0');
        const day = String(twoDaysBefore.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    checkAccess() {
        const hasAdminCarteraPermission = this._session.getUserAccess().includes(this.access[0]);
        const hasWebProfile = this._session.getUserProfileList().includes(this.access[1]);

        return hasAdminCarteraPermission && hasWebProfile;
    }

    getBagDetail(id: string) {
        this._apiService.getBagDetail(id).subscribe({
            next: (res) => {
                this.detail = res;
            },
            error: () => {
                this._alertsService.error("Ocurrió un error al intentar obtener el detalle de la cartera");
            }
        });
    }

    getBagData(id: string) {
        this.isModelosListLoading = true;

        return forkJoin({
            batchConfigs: this._apiService.getBatchConfigs(id),
            batchTemplates: this._apiService.listOrgBatchTemplates(),
        }).pipe(
            tap(results => {
                const batchConfigList = results.batchConfigs;

                this.batchConfigs = batchConfigList.batchConfigs || [];
                this.modelos = results.batchTemplates.batchTemplates || [];

                this.isModelosListLoading = false;
            },
            catchError(() => {
                this._alertsService.error("Ocurrió un error al intentar obtener los datos de la cartera");
                this.isModelosListLoading = false;
                return EMPTY;
            })
        ));
    }

    formatDateToISO(field: string, dateString: string): string {
        // Create a new Date object from the YYYY-MM-DD string
        const date = new Date(dateString);

        // Set the time (you can adjust this part if you want specific times)
        if (field === 'fechaFin') {
            date.setHours(23, 59, 59); // Set hours, minutes, seconds as per your need
        }

        // Format the date back to YYYY-MM-DDTHH:mm:ss
        const formattedDate = date.toISOString().slice(0, 19); // Taking only the date and time part

        return formattedDate;
    }

    searchProcesses() {
        this.isProcessesListLoading = true;

        const dateFromValue = this.filtersForm.value.dateFrom;
        const dateToValue = this.filtersForm.value.dateTo;

        const filterParams = {
            ...this.filtersForm.value,
            dateFrom: dateFromValue ? this.formatDateToISO('fechaInicio', dateFromValue) : undefined,
            dateTo: dateToValue ? this.formatDateToISO('fechaFin', dateToValue) : undefined,
            bagId: this.id,
        }

        this._apiService.listBatchExecutions(filterParams).subscribe((data) => {
            if (data.message) {
                this._alertsService.error("No se han encontrado ejecuciones para esta cartera");
                this.isProcessesListLoading = false;
                return;
            }

            const executions = data;
            this.procesos = this.mergeArrays(this.batchConfigs, executions);
            this.isProcessesListLoading = false;

            this.updateDataSource();

        },
        () => {
            this._alertsService.error("Error al obtener datos de cartera");
            this.isProcessesListLoading = false;
        })
    }

    goBack() {
        this._router.navigate(['/administracion-cartera-detalle', this.id]);
    }

    viewExecDetail(id: string, configId: string) {
        // Desenfoca el botón activo para evitar problemas de accesibilidad
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();

        this.dialog.open(ExecDetailModalComponent, {
            autoFocus: false,
            minWidth: '410px',
            data: {
                id: id,
                configId: configId
            }
        });
    }

    private mergeArrays(batchArray: BatchConfigListItem[], executionArray: BatchProcess[]): MergedExecutionItem[] {
        return executionArray.map(execution => {
            const matchingBatch = batchArray.find(batch => batch.id === execution.batchConfigId);

            return {
                id: execution.id,
                creationDate: execution.creationDate,
                status: execution.status,
                batchTemplate: matchingBatch?.batchTemplate,
                frequency: matchingBatch?.frequency
            };

        });
    }

    updateDataSource() {
        setTimeout(() => {
            this.dataSource = new MatTableDataSource<MergedExecutionItem>(this.procesos);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        }, 0);
    }
}
